/** @format */

import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Eye,
  History,
  ImagePlus,
  Loader2,
  Pencil,
  ScanSearch,
  Settings,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  rmaService,
  RMAAnalysisResult,
  RMAAnalysisSummary,
  RMAAnalysisResponse,
  RMAAnalysisStatus,
  RMASopStats,
} from "../../api/rmaService";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import Toast from "../../components/ui/Toast";
import { useOrgRole } from "../../hooks/useOrgRole";
import { useToast } from "../../hooks/useToast";
import { useSopStore } from "../../store/useSopStore";
import { uploadFileToS3 } from "../../utils/uploadFileToS3";
import "../sop-dashboard/SopDashBoard.css";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_FILE_SIZE_MB = 10;
const MAX_IMAGES = 4;

interface RMAUploadEntry {
  localId: string;
  file: File;
  previewUrl: string;
  uploading: boolean;
  mediaId?: string;
  error?: string;
}

function makeId() {
  return Math.random().toString(36).slice(2);
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type))
    return "Unsupported format. Use JPEG, PNG, WebP or GIF.";
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024)
    return `File too large (max ${MAX_FILE_SIZE_MB} MB).`;
  return null;
}

/** Classify requires confidence 0–100 and non-empty assessment (reasoning). */
function isRmaClassifyComplete(r: RMAAnalysisResult): boolean {
  if (r.confidence == null) return false;
  return typeof r.reasoning === "string" && r.reasoning.trim().length > 0;
}

/** Integer 0–100 only: strips non-digits and clamps to the valid range. */
function maskConfidence0to100(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n)) return "";
  return String(Math.min(100, Math.max(0, n)));
}

function formatDate(isoString: string | null | undefined): string {
  if (!isoString) return "—";
  const date = new Date(isoString.endsWith("Z") ? isoString : `${isoString}Z`);
  const locale = navigator.language;
  return (
    date.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  );
}

const STATUS_CONFIG: Record<
  RMAAnalysisStatus,
  { label: string; color: string; border: string; bg: string }
> = {
  pending_review: {
    label: "Processing",
    color: "text-amber-300",
    border: "border-amber-500/30",
    bg: "bg-amber-500/10",
  },
  approved: {
    label: "Released",
    color: "text-emerald-300",
    border: "border-emerald-500/30",
    bg: "bg-emerald-500/10",
  },
  rejected: {
    label: "Rejected",
    color: "text-red-300",
    border: "border-red-500/30",
    bg: "bg-red-500/10",
  },
};

function StatusBadge({ status }: { status: RMAAnalysisStatus }) {
  const st = status ?? "pending_review";
  if (st === "approved") return null;
  const cfg = STATUS_CONFIG[st] ?? STATUS_CONFIG.pending_review;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cfg.color} ${cfg.border} ${cfg.bg}`}
    >
      {st === "pending_review" && <Clock className="w-3 h-3" />}
      {st === "rejected" && <XCircle className="w-3 h-3" />}
      {cfg.label}
    </span>
  );
}

function RmaResultReviewCard({
  result,
  canEdit,
  onSave,
  saving,
}: {
  result: RMAAnalysisResult;
  canEdit: boolean;
  onSave: (
    resultId: string,
    payload: {
      verdict: "pass" | "fail";
      reasoning: string | null;
      confidence: number | null;
    },
  ) => void;
  saving: boolean;
}) {
  const rid = result.id ?? result.media_id;
  const [verdict, setVerdict] = useState<"pass" | "fail">(() => result.verdict);
  const [reason, setReason] = useState(() => result.reasoning ?? "");
  const [confStr, setConfStr] = useState(() => {
    const c = result.confidence;
    return c != null ? String(Math.round(c)) : "";
  });

  const canSubmitClassification = useMemo(() => {
    if (reason.trim() === "") return false;
    if (confStr.trim() === "") return false;
    const n = parseFloat(confStr);
    return Number.isFinite(n) && n >= 0 && n <= 100;
  }, [confStr, reason]);

  return (
    <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
      <div className="relative aspect-[4/3] bg-black/40">
        {result.url ? (
          <img src={result.url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImagePlus className="w-10 h-10 text-slate-600" />
          </div>
        )}
        {/* Badge when classification is complete (confidence + assessment) */}
        {isRmaClassifyComplete(result) && (
          <div
            className={`absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center shadow-lg ${
              result.verdict === "pass" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {result.verdict === "pass" ? (
              <CheckCircle2 className="w-4 h-4 text-white" />
            ) : (
              <XCircle className="w-4 h-4 text-white" />
            )}
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        {canEdit ? (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
              Classify
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setVerdict("pass")}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold ${
                  verdict === "pass"
                    ? "bg-green-600 text-white"
                    : "bg-slate-700/60 text-slate-400"
                }`}
              >
                Pass
              </button>
              <button
                type="button"
                onClick={() => setVerdict("fail")}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold ${
                  verdict === "fail"
                    ? "bg-red-600 text-white"
                    : "bg-slate-700/60 text-slate-400"
                }`}
              >
                Fail
              </button>
            </div>
            <label className="block">
              <span className="text-[10px] text-slate-500">
                Confidence (0–100) <span className="text-amber-400/90">*</span>
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={confStr}
                onChange={(e) =>
                  setConfStr(maskConfidence0to100(e.target.value))
                }
                className="mt-0.5 w-full rounded-lg border border-slate-600/50 bg-slate-900/80 px-2 py-1.5 text-xs text-slate-200"
                placeholder="0–100"
                maxLength={3}
                autoComplete="off"
                required
                aria-required="true"
              />
            </label>
            <label className="block">
              <span className="text-[10px] text-slate-500">
                Assessment <span className="text-amber-400/90">*</span>
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="mt-0.5 w-full rounded-lg border border-slate-600/50 bg-slate-900/80 px-2 py-1.5 text-xs text-slate-200"
                placeholder="Describe the finding…"
                required
                aria-required="true"
              />
            </label>
            <button
              type="button"
              disabled={saving || !rid || !canSubmitClassification}
              onClick={() => {
                if (!rid || !canSubmitClassification) return;
                const n = parseFloat(confStr);
                onSave(rid, {
                  verdict,
                  reasoning: reason.trim(),
                  confidence: n,
                });
              }}
              className="w-full py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs font-medium text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        ) : isRmaClassifyComplete(result) ? (
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="text-slate-500">Verdict:</span>
              <span
                className={`font-semibold ${result.verdict === "pass" ? "text-green-400" : "text-red-400"}`}
              >
                {result.verdict.toUpperCase()}
              </span>
              {result.confidence != null && (
                <span className="text-slate-400">
                  · {Math.round(result.confidence)}%
                </span>
              )}
            </div>
            {result.reasoning && (
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium mb-0.5">
                  Assessment
                </p>
                <p className="text-slate-300 leading-relaxed">
                  {result.reasoning}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-500 italic">No classification yet</p>
        )}
      </div>
    </div>
  );
}

function RmaClassifyModal({
  result,
  saving,
  onClose,
  onSave,
}: {
  result: RMAAnalysisResult;
  saving: boolean;
  onClose: () => void;
  onSave: (
    resultId: string,
    payload: {
      verdict: "pass" | "fail";
      reasoning: string | null;
      confidence: number | null;
    },
  ) => void;
}) {
  const rid = result.id ?? result.media_id;
  const [verdict, setVerdict] = useState<"pass" | "fail">(() => result.verdict);
  const [reason, setReason] = useState(() => result.reasoning ?? "");
  const [confStr, setConfStr] = useState(() => {
    const c = result.confidence;
    return c != null ? String(Math.round(c)) : "";
  });

  const canSubmitClassification = useMemo(() => {
    if (reason.trim() === "") return false;
    if (confStr.trim() === "") return false;
    const n = parseFloat(confStr);
    return Number.isFinite(n) && n >= 0 && n <= 100;
  }, [confStr, reason]);

  const handleSave = () => {
    if (!rid || !canSubmitClassification) return;
    const n = parseFloat(confStr);
    onSave(rid, { verdict, reasoning: reason.trim(), confidence: n });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-700/60 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {result.url && (
          <div className="relative aspect-video bg-black/60 overflow-hidden">
            <img
              src={result.url}
              alt=""
              className="w-full h-full object-contain"
            />
          </div>
        )}
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-400">
              Classify
            </p>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setVerdict("pass")}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                verdict === "pass"
                  ? "bg-green-600 text-white"
                  : "bg-slate-700/60 text-slate-400 hover:bg-slate-700"
              }`}
            >
              Pass
            </button>
            <button
              type="button"
              onClick={() => setVerdict("fail")}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                verdict === "fail"
                  ? "bg-red-600 text-white"
                  : "bg-slate-700/60 text-slate-400 hover:bg-slate-700"
              }`}
            >
              Fail
            </button>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wide font-medium text-slate-500">
              Confidence (0–100) <span className="text-amber-400/90">*</span>
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={confStr}
              onChange={(e) =>
                setConfStr(maskConfidence0to100(e.target.value))
              }
              className="mt-1 w-full rounded-xl border border-slate-600/50 bg-slate-800/80 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              placeholder="0–100"
              maxLength={3}
              autoComplete="off"
              required
              aria-required="true"
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wide font-medium text-slate-500">
              Assessment <span className="text-amber-400/90">*</span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-xl border border-slate-600/50 bg-slate-800/80 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
              placeholder="Describe the finding…"
              required
              aria-required="true"
            />
          </label>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 text-sm text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving || !rid || !canSubmitClassification}
              onClick={handleSave}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50 ${
                verdict === "pass"
                  ? "bg-green-600 hover:bg-green-500"
                  : "bg-red-600 hover:bg-red-500"
              }`}
            >
              {saving
                ? "Saving…"
                : `Mark as ${verdict === "pass" ? "Pass" : "Fail"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const RMAAnalysisPage: FC = () => {
  const { sopId } = useParams<{ sopId: string }>();
  const navigate = useNavigate();
  const { canAccessRMA, canManageRMA, canPublishRMA, isRoleResolving } =
    useOrgRole();
  const { fetchSOPById, sops } = useSopStore();
  const { toast, showToast, hideToast } = useToast();

  // --- All state (must be declared before handlers that reference setters) ---
  const [historyList, setHistoryList] = useState<RMAAnalysisSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedAnalysis, setSelectedAnalysis] =
    useState<RMAAnalysisResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rmaEntries, setRmaEntries] = useState<RMAUploadEntry[]>([]);
  const [analyzeBusy, setAnalyzeBusy] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<RMAAnalysisResult[]>(
    [],
  );
  const [summary, setSummary] = useState<{
    total: number;
    passed: number;
    failed: number;
  } | null>(null);
  const [activeFilter, setActiveFilter] = useState<"all" | "pass" | "fail">(
    "all",
  );
  const [detailImage, setDetailImage] = useState<RMAAnalysisResult | null>(
    null,
  );
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [sopStats, setSopStats] = useState<RMASopStats | null>(null);
  const [savingResultId, setSavingResultId] = useState<string | null>(null);
  const [analyzeModalOpen, setAnalyzeModalOpen] = useState(false);
  const [analyzeModalCloseWarning, setAnalyzeModalCloseWarning] =
    useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  /** True when this SOP has at least one saved analysis (unfiltered list count from API). */
  const [hasRmaHistoryInDb, setHasRmaHistoryInDb] = useState(false);
  /** Pass/fail criteria images — required before running analysis. */
  const [datasetLoading, setDatasetLoading] = useState(true);
  const [hasCriteriaDataset, setHasCriteriaDataset] = useState(false);
  /** Which result the SA is currently classifying via the modal. */
  const [classifyingResult, setClassifyingResult] =
    useState<RMAAnalysisResult | null>(null);
  /** Result IDs the SA has explicitly classified in this session. */
  const [localClassified, setLocalClassified] = useState<Set<string>>(
    new Set(),
  );
  /** Result IDs flagged as needing classification on a failed publish attempt. */
  const [unclassifiedResultIds, setUnclassifiedResultIds] = useState<
    Set<string>
  >(new Set());

  const HISTORY_LIMIT = 4;

  // When an analysis is opened (or re-opened), seed localClassified from API data so
  // tiles that were already classified in a previous session appear correctly.
  useEffect(() => {
    if (!selectedAnalysis) return;
    const alreadyClassified = new Set(
      selectedAnalysis.results
        .filter((r) => isRmaClassifyComplete(r))
        .map((r) => r.id ?? r.media_id),
    );
    setLocalClassified(alreadyClassified);
    setUnclassifiedResultIds(new Set());
    setClassifyingResult(null);
  }, [selectedAnalysis?.analysis_id]);

  const closeAnalyzeModal = () => {
    setAnalyzeModalOpen(false);
    setAnalyzeModalCloseWarning(false);
    setRmaEntries([]);
    setAnalysisResults([]);
    setSummary(null);
    setActiveFilter("all");
    setAnalyzeError(null);
  };

  const requestCloseAnalyzeModal = () => {
    const hasWork =
      analyzeBusy ||
      rmaEntries.some((e) => e.uploading) ||
      (rmaEntries.length > 0 && !summary);
    if (hasWork) {
      setAnalyzeModalCloseWarning(true);
      return;
    }
    closeAnalyzeModal();
  };

  const rmaInputRef = useRef<HTMLInputElement>(null);

  const fetchSopStats = useCallback(async () => {
    if (!sopId) return;
    try {
      const s = await rmaService.getSopStats(sopId);
      setSopStats(s);
    } catch {
      setSopStats(null);
    }
  }, [sopId]);

  const fetchCriteriaDataset = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!sopId) return;
      if (!opts?.silent) setDatasetLoading(true);
      try {
        const list = await rmaService.getCriteria(sopId);
        setHasCriteriaDataset(Array.isArray(list) && list.length > 0);
      } catch {
        setHasCriteriaDataset(false);
      } finally {
        if (!opts?.silent) setDatasetLoading(false);
      }
    },
    [sopId],
  );

  const fetchHistory = useCallback(
    async (page = 1) => {
      if (!sopId) return;
      setHistoryLoading(true);
      try {
        const res = await rmaService.getAnalyses(sopId, {
          page,
          limit: HISTORY_LIMIT,
        });
        setHistoryList(Array.isArray(res.analyses) ? res.analyses : []);
        const total = typeof res.total === "number" ? res.total : 0;
        setHistoryTotal(total);
        setHistoryPage(page);
        setHasRmaHistoryInDb(total > 0);
      } catch {
        setHistoryList([]);
        setHistoryTotal(0);
      } finally {
        setHistoryLoading(false);
      }
    },
    [sopId],
  );

  const addRMAFiles = useCallback((files: FileList) => {
    setRmaEntries((prev) => {
      const slots = MAX_IMAGES - prev.length;
      if (slots <= 0) return prev;
      const incoming = Array.from(files).slice(0, slots);
      const entries: RMAUploadEntry[] = incoming.map((file) => ({
        localId: makeId(),
        file,
        previewUrl: URL.createObjectURL(file),
        uploading: false,
        error: validateFile(file) ?? undefined,
      }));
      return [...prev, ...entries];
    });
  }, []);

  useEffect(() => {
    if (!sopId || typeof fetchSOPById !== "function") return;
    if (!sops?.find((s) => s.sopId === sopId)) {
      void fetchSOPById(sopId).catch(() => {});
    }
  }, [sopId, sops, fetchSOPById]);

  useEffect(() => {
    void fetchHistory(historyPage);
  }, [fetchHistory, historyPage]);

  useEffect(() => {
    void fetchSopStats();
  }, [fetchSopStats]);

  useEffect(() => {
    void fetchCriteriaDataset();
  }, [fetchCriteriaDataset]);

  useEffect(() => {
    if (analyzeModalOpen && sopId) {
      void fetchCriteriaDataset({ silent: true });
    }
  }, [analyzeModalOpen, sopId, fetchCriteriaDataset]);

  const openAnalysisDetail = async (analysisId: string) => {
    if (!sopId) return;
    setDetailLoading(true);
    try {
      const detail = await rmaService.getAnalysisDetail(sopId, analysisId);
      setSelectedAnalysis(detail);
    } catch {
      // silently fail
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePublishAnalysis = async () => {
    if (
      !sopId ||
      !selectedAnalysis ||
      selectedAnalysis.status !== "pending_review"
    )
      return;

    // Every image needs confidence + assessment (API or saved this session).
    const missing = selectedAnalysis.results
      .filter(
        (r) =>
          !localClassified.has(r.id ?? r.media_id) &&
          !isRmaClassifyComplete(r),
      )
      .map((r) => r.id ?? r.media_id);
    if (missing.length > 0) {
      setUnclassifiedResultIds(new Set(missing));
      return;
    }

    setPublishBusy(true);
    setGlobalError(null);
    try {
      await rmaService.publishAnalysis(sopId, selectedAnalysis.analysis_id);
      setSelectedAnalysis(null);
      void fetchHistory(historyPage);
      void fetchSopStats();
    } catch (err) {
      setGlobalError(err instanceof Error ? err.message : "Could not publish.");
    } finally {
      setPublishBusy(false);
    }
  };

  const handleSaveResultEdit = async (
    resultId: string,
    payload: {
      verdict: "pass" | "fail";
      reasoning: string | null;
      confidence: number | null;
    },
  ) => {
    if (!sopId || !selectedAnalysis) return;
    setSavingResultId(resultId);
    setGlobalError(null);
    try {
      await rmaService.editResult(
        sopId,
        selectedAnalysis.analysis_id,
        resultId,
        {
          verdict: payload.verdict,
          reasoning: payload.reasoning,
          confidence: payload.confidence,
        },
      );
      const detail = await rmaService.getAnalysisDetail(
        sopId,
        selectedAnalysis.analysis_id,
      );
      setSelectedAnalysis(detail);
      void fetchSopStats();
      void fetchHistory(historyPage);
      // Mark this result as classified and close the modal.
      setLocalClassified((prev) => new Set([...prev, resultId]));
      setUnclassifiedResultIds((prev) => {
        const next = new Set(prev);
        next.delete(resultId);
        return next;
      });
      setClassifyingResult(null);
    } catch (err) {
      setGlobalError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setSavingResultId(null);
    }
  };

  if (isRoleResolving) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }
  if (!canAccessRMA) {
    return <Navigate to="/sop-dashboard" replace />;
  }

  const sopRow = sops?.find((s) => s.sopId === sopId);
  const sopTitle =
    (typeof sopRow?.getDisplayTitle === "function"
      ? sopRow.getDisplayTitle()
      : null) ??
    sopId ??
    "SOP";

  const removeRMAEntry = (localId: string) =>
    setRmaEntries((p) => p.filter((e) => e.localId !== localId));

  const handleAnalyze = async () => {
    if (sopId) {
      try {
        const list = await rmaService.getCriteria(sopId);
        if (!Array.isArray(list) || list.length === 0) {
          setHasCriteriaDataset(false);
          setAnalyzeError(
            "Add at least one pass or fail example to the criteria dataset before running an analysis.",
          );
          return;
        }
        setHasCriteriaDataset(true);
      } catch {
        setAnalyzeError("Could not verify the criteria dataset. Please try again.");
        return;
      }
    }
    const valid = rmaEntries.filter((e) => !e.error);
    if (!valid.length) {
      setAnalyzeError("Upload at least one RMA image to analyze.");
      return;
    }
    setAnalyzeError(null);
    setAnalyzeBusy(true);

    const mediaIds: string[] = [];
    for (const entry of valid) {
      setRmaEntries((p) =>
        p.map((e) =>
          e.localId === entry.localId ? { ...e, uploading: true } : e,
        ),
      );
      try {
        const res = await uploadFileToS3(entry.file, sopId);
        if (!res || typeof res === "string") throw new Error("Upload failed");
        mediaIds.push(res.media_id);
        setRmaEntries((p) =>
          p.map((e) =>
            e.localId === entry.localId
              ? { ...e, uploading: false, mediaId: res.media_id }
              : e,
          ),
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        setRmaEntries((p) =>
          p.map((e) =>
            e.localId === entry.localId
              ? { ...e, uploading: false, error: msg }
              : e,
          ),
        );
      }
    }

    if (!mediaIds.length) {
      setAnalyzeError("All uploads failed. Please try again.");
      showToast("Upload failed", "All images failed to upload. Please retry.", "error");
      setAnalyzeBusy(false);
      return;
    }

    try {
      const response = await rmaService.analyze(sopId!, mediaIds);
      // API returns no per-image results until published; inline block only if a future API returns rows.
      if (response.results && response.results.length > 0) {
        setAnalysisResults(
          Array.isArray(response.results) ? response.results : [],
        );
        setSummary({
          total: response.total,
          passed: response.passed ?? 0,
          failed: response.failed ?? 0,
        });
        setActiveFilter("all");
      } else {
        setAnalysisResults([]);
        setSummary(null);
        setActiveFilter("all");
      }
      setRmaEntries([]);
      void fetchHistory(1);
      void fetchSopStats();
      showToast(
        "Analysis submitted",
        "Your images were submitted successfully.",
        "success",
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Analysis failed. Please retry.";
      setAnalyzeError(msg);
      showToast("Analysis failed", msg, "error");
    } finally {
      setAnalyzeBusy(false);
    }
  };

  const safeAnalysisResults = analysisResults ?? [];
  const safeHistoryList = historyList ?? [];

  const filtered =
    activeFilter === "all"
      ? safeAnalysisResults
      : safeAnalysisResults.filter((r) => r.verdict === activeFilter);

  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_LIMIT));

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />

      <main className="pt-20 px-4 pb-16">
        <div className="max-w-6xl mx-auto pt-0 w-full space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate("/rma-dashboard")}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to RMA Dashboard
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={datasetLoading || !hasCriteriaDataset}
                title={
                  !datasetLoading && !hasCriteriaDataset
                    ? "Add a criteria dataset (pass/fail examples) before running an analysis."
                    : undefined
                }
                onClick={() => setAnalyzeModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white text-sm font-semibold shadow-lg shadow-blue-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-cyan-600"
              >
                {datasetLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Run Analysis
              </button>
              {canManageRMA && (
                <button
                  type="button"
                  onClick={() => navigate(`/sop/${sopId}/rma/dataset`)}
                  className="px-4 py-2 rounded-xl border border-slate-600/60 text-sm text-slate-200 hover:text-white hover:border-slate-500 transition-colors flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Manage Dataset
                </button>
              )}
            </div>
          </div>

          <HeroSection
            title="RMA Defect Analysis"
            description={sopTitle}
            icons={[<ScanSearch className="w-12 h-12 text-cyan-400" />, null]}
          />

          {!datasetLoading && !hasCriteriaDataset && (
            <div className="rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-100">
                    Criteria dataset required
                  </p>
                  <p className="text-sm text-amber-100/85 mt-1">
                    To run an analysis, this SOP needs at least one pass or fail
                    example in the RMA criteria dataset.
                  </p>
                </div>
              </div>
              {canManageRMA && sopId && (
                <button
                  type="button"
                  onClick={() => navigate(`/sop/${sopId}/rma/dataset`)}
                  className="shrink-0 px-4 py-2 rounded-lg border border-amber-500/40 bg-amber-500/15 text-amber-100 text-sm font-medium hover:bg-amber-500/25 transition-colors"
                >
                  Manage dataset
                </button>
              )}
            </div>
          )}

          <div className="bg-gradient-to-br from-gray-900/20 to-black/40 backdrop-blur-xl border border-gray-800/15 rounded-2xl p-6 sm:p-8 shadow-2xl shadow-black/50 space-y-8">
            {/* ---- SOP-wide stats (released analyses only) ---- */}
            {sopStats && (
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-slate-700/30 bg-slate-800/30 p-3 flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-slate-700/50 flex items-center justify-center min-w-[1.75rem] min-h-[1.75rem]">
                    <Eye className="w-4 h-4 text-slate-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold text-white truncate">
                      {sopStats.total}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5 whitespace-nowrap">
                      Total
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border border-green-500/25 bg-green-500/5 p-3 flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-green-500/15 flex items-center justify-center min-w-[1.75rem] min-h-[1.75rem]">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold text-green-400 truncate">
                      {sopStats.passed}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5 whitespace-nowrap">
                      Passed
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border border-red-500/25 bg-red-500/5 p-3 flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-red-500/15 flex items-center justify-center min-w-[1.75rem] min-h-[1.75rem]">
                    <XCircle className="w-4 h-4 text-red-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xl font-semibold text-red-400 truncate">
                      {sopStats.failed}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5 whitespace-nowrap">
                      Failed
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ---- Analysis History (only when this SOP has at least one saved analysis) ---- */}
            {hasRmaHistoryInDb && (
              <div className="rounded-2xl border border-slate-700/40 bg-slate-800/20 p-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-400" />
                    <h2 className="text-lg font-semibold text-white">
                      Analysis History
                    </h2>
                    <span className="text-xs text-slate-500">
                      ({historyTotal})
                    </span>
                  </div>
                </div>

                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                  </div>
                ) : safeHistoryList.length === 0 ? null : (
                  <>
                    <div className="space-y-10">
                      {safeHistoryList.map((a) => {
                        const tiles: Array<{
                          url: string;
                          verdict?: "pass" | "fail";
                        }> =
                          a.preview_items && a.preview_items.length > 0
                            ? a.preview_items
                            : (a.preview_urls ?? []).map((url) => ({ url }));
                        return (
                          <div key={a.analysis_id} className="space-y-3">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-400">
                              <StatusBadge status={a.status} />
                              <span className="text-slate-300">
                                {formatDate(a.created_at)}
                              </span>
                              <span className="text-xs text-slate-500">
                                {a.status === "approved" || canPublishRMA
                                  ? `${a.passed} pass · ${a.failed} fail · ${a.total} images`
                                  : `${a.total} image${a.total === 1 ? "" : "s"}`}
                              </span>
                            </div>
                            {(() => {
                              // Non-SA users cannot open a "Processing" analysis — no results to show yet.
                              const isClickable =
                                a.status === "approved" || canPublishRMA;
                              return tiles.length > 0 ? (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 w-full min-w-0">
                                  {tiles.map((tile, idx) => {
                                    const v = tile.verdict;
                                    // Only colour-code verdicts on approved analyses; pending ones haven't been confirmed yet.
                                    const hasVerdict =
                                      a.status === "approved" &&
                                      (v === "pass" || v === "fail");
                                    return (
                                      <button
                                        key={`${a.analysis_id}-p-${idx}`}
                                        type="button"
                                        disabled={!isClickable}
                                        onClick={
                                          isClickable
                                            ? () =>
                                                void openAnalysisDetail(
                                                  a.analysis_id,
                                                )
                                            : undefined
                                        }
                                        className={`relative aspect-square overflow-hidden rounded-lg border border-slate-700/50 bg-slate-900/60 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 ${!isClickable ? "cursor-default" : ""}`}
                                      >
                                        <img
                                          src={tile.url}
                                          alt=""
                                          className="h-full w-full object-cover"
                                          loading="lazy"
                                        />
                                        {hasVerdict ? (
                                          <>
                                            <div
                                              className={`absolute inset-x-0 top-0 h-1 ${
                                                v === "pass"
                                                  ? "bg-green-500"
                                                  : "bg-red-500"
                                              }`}
                                            />
                                            <div
                                              className={`absolute inset-x-0 bottom-0 py-1 text-center text-[10px] font-bold uppercase tracking-wide text-white ${
                                                v === "pass"
                                                  ? "bg-green-600/90"
                                                  : "bg-red-600/90"
                                              }`}
                                            >
                                              {v === "pass" ? "Pass" : "Fail"}
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div className="absolute inset-x-0 top-0 h-1 bg-slate-600/60" />
                                            <div className="absolute inset-0 bg-slate-900/30" />
                                            <div className="absolute inset-x-0 bottom-0 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-800/80">
                                              Processing
                                            </div>
                                          </>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  disabled={!isClickable}
                                  onClick={
                                    isClickable
                                      ? () =>
                                          void openAnalysisDetail(a.analysis_id)
                                      : undefined
                                  }
                                  className={`w-full rounded-lg border border-dashed border-slate-600/50 py-8 text-sm text-slate-500 ${isClickable ? "hover:border-slate-500 hover:text-slate-300" : "cursor-default"}`}
                                >
                                  {a.total} image{a.total === 1 ? "" : "s"}
                                  {isClickable ? " — tap to open" : ""}
                                </button>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                    {historyTotalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 pt-2">
                        <button
                          type="button"
                          disabled={historyPage <= 1}
                          onClick={() => void fetchHistory(historyPage - 1)}
                          className="px-3 py-1 rounded-lg border border-slate-600/50 text-slate-300 text-xs disabled:opacity-40"
                        >
                          Previous
                        </button>
                        <span className="text-xs text-slate-500">
                          Page {historyPage} of {historyTotalPages}
                        </span>
                        <button
                          type="button"
                          disabled={historyPage >= historyTotalPages}
                          onClick={() => void fetchHistory(historyPage + 1)}
                          className="px-3 py-1 rounded-lg border border-slate-600/50 text-slate-300 text-xs disabled:opacity-40"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      </main>

      {/* ---- Image detail modal (inline analysis results) ---- */}
      {detailImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setDetailImage(null)}
        >
          <div
            className="bg-slate-900 border border-slate-700/60 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {detailImage.url && (
              <img
                src={detailImage.url}
                alt="detail"
                className="w-full max-h-72 object-contain bg-black"
              />
            )}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                {detailImage.verdict === "pass" ? (
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <span
                  className={`font-bold text-lg ${detailImage.verdict === "pass" ? "text-green-400" : "text-red-400"}`}
                >
                  {detailImage.verdict === "pass" ? "PASS" : "FAIL"}
                </span>
                {detailImage.confidence != null && (
                  <span className="text-slate-400 text-sm ml-1">
                    ({Math.round(detailImage.confidence)}% confidence)
                  </span>
                )}
              </div>
              {detailImage.reasoning && (
                <p className="text-sm text-slate-300 leading-relaxed">
                  {detailImage.reasoning}
                </p>
              )}
              <button
                type="button"
                onClick={() => setDetailImage(null)}
                className="mt-5 w-full py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Analysis detail modal (from history click) ---- */}
      {(detailLoading || selectedAnalysis) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => {
            setSelectedAnalysis(null);
            setDetailLoading(false);
          }}
        >
          <div
            className="bg-slate-900 border border-slate-700/60 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {detailLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              </div>
            ) : selectedAnalysis ? (
              <div className="p-6 space-y-5">
                {globalError && (
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-center gap-2">
                    <XCircle className="w-4 h-4 shrink-0" />
                    <span className="flex-1">{globalError}</span>
                    <button
                      type="button"
                      onClick={() => setGlobalError(null)}
                      className="text-red-400 hover:text-red-200 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white flex gap-4 items-center">
                      Analysis Detail{" "}
                      <StatusBadge status={selectedAnalysis.status} />
                    </h3>
                    <p className="text-sm text-slate-400">
                      {formatDate(selectedAnalysis.created_at)}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    {canPublishRMA &&
                      selectedAnalysis.status === "pending_review" && (
                        <div className="flex flex-col items-end gap-1">
                          <button
                            type="button"
                            disabled={publishBusy}
                            onClick={() => void handlePublishAnalysis()}
                            className="px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50"
                          >
                            {publishBusy ? "…" : "Send Analysis"}
                          </button>
                          {unclassifiedResultIds.size > 0 && (
                            <p className="text-[11px] text-orange-400">
                              {unclassifiedResultIds.size} image
                              {unclassifiedResultIds.size === 1 ? "" : "s"}{" "}
                              still need classification
                            </p>
                          )}
                        </div>
                      )}
                    <button
                      type="button"
                      onClick={() => setSelectedAnalysis(null)}
                      className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-300">
                    <span className="text-white font-semibold">
                      {selectedAnalysis.total}
                    </span>{" "}
                    images ·{" "}
                    <span className="text-green-400">
                      {selectedAnalysis.passed ?? "—"} pass
                    </span>{" "}
                    ·{" "}
                    <span className="text-red-400">
                      {selectedAnalysis.failed ?? "—"} fail
                    </span>
                  </p>
                </div>

                {canPublishRMA &&
                selectedAnalysis.status === "pending_review" ? (
                  /* SA classify view — image tiles with paintbrush icon */
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {selectedAnalysis.results.map((r) => {
                      const rid = r.id ?? r.media_id;
                      const isClassified =
                        localClassified.has(rid) || isRmaClassifyComplete(r);
                      const isUnclassified = unclassifiedResultIds.has(rid);
                      return (
                        <div
                          key={rid}
                          className={`relative aspect-square overflow-hidden rounded-xl bg-slate-900/60 border transition-colors ${
                            isUnclassified
                              ? "border-orange-500"
                              : "border-slate-700/50"
                          }`}
                        >
                          {r.url ? (
                            <img
                              src={r.url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImagePlus className="w-8 h-8 text-slate-600" />
                            </div>
                          )}

                          {/* Orange top bar when flagged as missing classification */}
                          {isUnclassified && (
                            <div className="absolute inset-x-0 top-0 h-1 bg-orange-500" />
                          )}

                          {/* Verdict label once classified */}
                          {isClassified && (
                            <div
                              className={`absolute inset-x-0 bottom-0 py-1 text-center text-[10px] font-bold uppercase tracking-wide text-white ${
                                r.verdict === "pass"
                                  ? "bg-green-600/90"
                                  : "bg-red-600/90"
                              }`}
                            >
                              {r.verdict === "pass" ? "Pass" : "Fail"}
                            </div>
                          )}

                          {/* Paintbrush button */}
                          <button
                            type="button"
                            title="Classify this image"
                            onClick={() => setClassifyingResult(r)}
                            className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                              isClassified
                                ? r.verdict === "pass"
                                  ? "bg-green-500 hover:bg-green-400"
                                  : "bg-red-500 hover:bg-red-400"
                                : "bg-slate-700/80 hover:bg-slate-600"
                            }`}
                          >
                            <Pencil className="w-3.5 h-3.5 text-white" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Read-only view */
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedAnalysis.results.map((r) => (
                      <RmaResultReviewCard
                        key={r.id ?? r.media_id}
                        result={r}
                        canEdit={false}
                        saving={false}
                        onSave={() => {}}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* ---- New Analysis modal ---- */}
      {analyzeModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={requestCloseAnalyzeModal}
        >
          <div
            className="bg-slate-900 border border-slate-700/60 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/40 shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  New Analysis
                </h2>
                <p className="text-sm text-slate-400 mt-0.5">
                  Upload images to run a new defect analysis.
                </p>
              </div>
              <button
                type="button"
                onClick={requestCloseAnalyzeModal}
                className="w-8 h-8 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center shrink-0"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Close warning */}
              {analyzeModalCloseWarning && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
                  <p className="text-sm text-amber-300 font-medium">
                    {analyzeBusy || rmaEntries.some((e) => e.uploading)
                      ? "Analysis is in progress. Results will still appear in History when complete."
                      : "You have unsaved images. Close and discard them?"}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAnalyzeModalCloseWarning(false)}
                      className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm text-white font-medium transition-colors"
                    >
                      Keep open
                    </button>
                    <button
                      type="button"
                      onClick={closeAnalyzeModal}
                      className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-sm text-white font-semibold transition-colors"
                    >
                      Close anyway
                    </button>
                  </div>
                </div>
              )}

              {/* Error banner */}
              {analyzeError && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 flex items-center gap-2">
                  <XCircle className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{analyzeError}</span>
                  <button
                    type="button"
                    onClick={() => setAnalyzeError(null)}
                    className="text-red-400 hover:text-red-200 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {!summary ? (
                <>
                  {/* Analyze button */}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAnalyze}
                      disabled={
                        analyzeBusy ||
                        rmaEntries.filter((e) => !e.error).length === 0
                      }
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20"
                    >
                      {analyzeBusy ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          Analyze Images
                        </>
                      )}
                    </button>
                  </div>

                  {/* Dropzone */}
                  <button
                    type="button"
                    disabled={analyzeBusy || rmaEntries.length >= MAX_IMAGES}
                    onClick={() => rmaInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-blue-500/40 hover:border-blue-400/70 hover:bg-blue-500/5 rounded-2xl p-10 flex flex-col items-center gap-4 text-slate-400 hover:text-slate-200 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-12 h-12 text-blue-400" />
                    <div className="text-center">
                      <p className="font-semibold text-white text-base">
                        Click to upload RMA images
                      </p>
                      <p className="text-xs mt-1">
                        JPEG, PNG, WebP, GIF — max {MAX_FILE_SIZE_MB} MB each ·{" "}
                        {rmaEntries.length}/{MAX_IMAGES} selected
                      </p>
                    </div>
                  </button>
                  <input
                    ref={rmaInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(",")}
                    multiple
                    className="hidden"
                    onChange={(e) =>
                      e.target.files && addRMAFiles(e.target.files)
                    }
                  />

                  {/* Staged files grid */}
                  {rmaEntries.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {rmaEntries.map((entry) => (
                        <div
                          key={entry.localId}
                          className={`relative rounded-xl overflow-hidden border ${
                            entry.error
                              ? "border-red-500/40"
                              : "border-slate-700/40"
                          } bg-slate-800/40`}
                        >
                          <img
                            src={entry.previewUrl}
                            alt={entry.file.name}
                            className="w-full h-36 object-cover"
                          />
                          {entry.uploading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <Loader2 className="w-6 h-6 text-white animate-spin" />
                            </div>
                          )}
                          {!entry.uploading && (
                            <button
                              type="button"
                              onClick={() => removeRMAEntry(entry.localId)}
                              className="absolute top-2 right-2 w-6 h-6 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          )}
                          <div className="px-2 py-2">
                            <p className="text-xs text-slate-300 truncate">
                              {entry.file.name}
                            </p>
                            {entry.error && (
                              <p className="text-xs text-red-400 mt-0.5">
                                {entry.error}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                /* ---- Inline results ---- */
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-slate-300">
                      <span className="text-white font-semibold">
                        {summary.total}
                      </span>{" "}
                      images ·{" "}
                      <span className="text-green-400 font-medium">
                        {summary.passed} pass
                      </span>{" "}
                      ·{" "}
                      <span className="text-red-400 font-medium">
                        {summary.failed} fail
                      </span>
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {(
                      [
                        { key: "all", label: `All (${summary.total})` },
                        { key: "pass", label: `Pass (${summary.passed})` },
                        { key: "fail", label: `Fail (${summary.failed})` },
                      ] as const
                    ).map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setActiveFilter(key)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                          activeFilter === key
                            ? "bg-slate-700 text-white border border-slate-600/60"
                            : "text-slate-400 hover:text-white border border-transparent"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 w-full min-w-0">
                    {filtered.map((result) => {
                      const v = result.verdict;
                      const hasRealVerdict = isRmaClassifyComplete(result);
                      return (
                        <button
                          key={result.id ?? result.media_id}
                          type="button"
                          onClick={() => setDetailImage(result)}
                          className="relative aspect-square overflow-hidden rounded-lg border border-slate-700/40 bg-slate-800/40 text-left transition hover:border-slate-500/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 group"
                        >
                          {result.url ? (
                            <img
                              src={result.url}
                              alt="RMA result"
                              className="h-full w-full object-cover group-hover:opacity-95 transition-opacity"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-slate-700/50">
                              <ImagePlus className="w-8 h-8 text-slate-500" />
                            </div>
                          )}
                          {hasRealVerdict ? (
                            <>
                              <div
                                className={`absolute inset-x-0 top-0 h-1 ${v === "pass" ? "bg-green-500" : "bg-red-500"}`}
                              />
                              <div
                                className={`absolute inset-x-0 bottom-0 py-1 text-center text-[10px] font-bold uppercase tracking-wide text-white ${
                                  v === "pass"
                                    ? "bg-green-600/90"
                                    : "bg-red-600/90"
                                }`}
                              >
                                {v === "pass" ? "Pass" : "Fail"}
                              </div>
                              {result.confidence != null && (
                                <div className="absolute left-1 top-2 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                  {Math.round(result.confidence)}%
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="absolute inset-x-0 top-0 h-1 bg-slate-600/60" />
                              <div className="absolute inset-0 bg-slate-900/30" />
                              <div className="absolute inset-x-0 bottom-0 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-800/80">
                                Pending
                              </div>
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex justify-center pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setRmaEntries([]);
                        setAnalysisResults([]);
                        setSummary(null);
                        setActiveFilter("all");
                      }}
                      className="px-5 py-2.5 rounded-xl border border-slate-600/50 text-slate-300 hover:text-white hover:border-slate-500 transition-colors text-sm"
                    >
                      Run another analysis
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Toast
        open={toast.open}
        onOpenChange={hideToast}
        title={toast.title ?? ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />

      {/* ---- Classify modal (SA only, sits above the detail modal) ---- */}
      {classifyingResult && (
        <RmaClassifyModal
          result={classifyingResult}
          saving={savingResultId === (classifyingResult.id ?? classifyingResult.media_id)}
          onClose={() => setClassifyingResult(null)}
          onSave={(resultId, payload) =>
            void handleSaveResultEdit(resultId, payload)
          }
        />
      )}
    </div>
  );
};

export default RMAAnalysisPage;
